import { useEffect, useMemo, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowLeft,
  BarChart3,
  Download,
  Loader2,
  LogOut,
  ShieldCheck,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { LogoSymbol, LogoWordmark } from "../components/Logo";
import { adminApi, useAdminAuth } from "../lib/adminAuth";

const RANGE_OPTIONS = [
  { label: "7d", days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 },
];

const EVENT_LABELS = {
  nudge_save_med_clicked: "Nudge opened",
  nudge_save_med_expanded: "Picker loaded",
  nudge_save_med_saved: "Med saved",
  change_med_clicked: "Change tapped",
  routine_sample_viewed: "Sample viewed",
  check_completed: "Check completed",
};

function fmtNum(n) {
  return (n ?? 0).toLocaleString("en-US");
}

export default function AdminEvents() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(14);
  const [summary, setSummary] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // When from/to are set, they override `days` for the CSV export.
  // The dashboard tiles always scope to the rolling `days` window for sanity.
  const hasDateRange = Boolean(fromDate || toDate);
  const dateRangeInvalid =
    fromDate && toDate && fromDate > toDate;

  function buildExportQuery(includeKey) {
    const parts = [`include=${includeKey}`];
    if (fromDate) parts.push(`from=${fromDate}`);
    if (toDate) parts.push(`to=${toDate}`);
    if (!fromDate && !toDate) parts.push(`days=${days}`);
    return parts.join("&");
  }

  function buildExportFilename(includeKey) {
    const today = new Date().toISOString().slice(0, 10);
    if (fromDate || toDate) {
      const f = fromDate || "earliest";
      const t = toDate || today;
      return `blendsafe_${includeKey}_${f}_to_${t}.csv`;
    }
    return `blendsafe_${includeKey}_${days}d_${today}.csv`;
  }

  async function handleExportCsv(includeKey = "events") {
    if (exporting) return;
    if (dateRangeInvalid) {
      toast.error("'From' must be on or before 'To'");
      return;
    }
    setExporting(true);
    try {
      const res = await adminApi().get(
        `/events/export.csv?${buildExportQuery(includeKey)}`,
        { responseType: "blob" },
      );
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = buildExportFilename(includeKey);
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${includeKey} CSV`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Export failed: ${msg}`);
    } finally {
      setExporting(false);
    }
  }

  const [rumData, setRumData] = useState(null);
  const [rumAlertStatus, setRumAlertStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      try {
        const [s, r, rum, alertsStatus] = await Promise.all([
          adminApi().get(`/events/summary?days=${days}`),
          adminApi().get(`/events/recent?limit=50`),
          adminApi().get(`/events/rum?days=${days}`).catch(() => ({ data: null })),
          adminApi().get(`/events/rum/alerts/status`).catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setRecent(r.data?.rows || []);
        setRumData(rum.data);
        setRumAlertStatus(alertsStatus.data);
      } catch (e) {
        if (cancelled) return;
        const msg = e?.response?.data?.detail || e.message;
        setError(String(msg));
        toast.error(`Failed to load analytics: ${msg}`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [days]);

  const conversion = useMemo(() => {
    if (!summary?.events) return null;
    const byName = Object.fromEntries(
      summary.events.map((e) => [e.event_name, e]),
    );
    const clicked = byName.nudge_save_med_clicked?.total || 0;
    const saved = byName.nudge_save_med_saved?.total || 0;
    if (!clicked) return null;
    return Math.round((saved / clicked) * 100);
  }, [summary]);

  return (
    <main
      data-testid="admin-events-page"
      className="min-h-screen bg-[#fdf8f3] px-6 py-12"
    >
      <div className="mx-auto max-w-6xl">
        <header className="mb-10 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <LogoSymbol size={32} />
            <LogoWordmark size={18} />
            <span className="ml-2 inline-flex items-center gap-1.5 rounded-full border border-stone-300 bg-white px-2.5 py-0.5 text-[10px] uppercase tracking-wider text-stone-600">
              <ShieldCheck className="h-3 w-3" /> Admin
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-stone-500 sm:block">
              {admin?.email}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                logout();
                navigate("/admin", { replace: true });
              }}
              data-testid="admin-logout"
            >
              <LogOut className="mr-1.5 h-4 w-4" /> Sign out
            </Button>
          </div>
        </header>

        <AdminHeader />

        <div className="mt-2 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-stone-900">
              Analytics Events
            </h1>
            <p className="mt-1 text-sm text-stone-600">
              Conversion signals from the product UI, grouped by event name and
              day.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div
              data-testid="events-date-range"
              className="flex items-center gap-1.5 text-xs"
            >
              <span className="text-[10.5px] uppercase tracking-wider text-stone-500">
                Export window
              </span>
              <input
                type="date"
                data-testid="events-date-from"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
                className="h-7 rounded-md border border-stone-200 bg-white px-2 text-xs text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300"
                aria-label="From date"
              />
              <span className="text-stone-400">→</span>
              <input
                type="date"
                data-testid="events-date-to"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
                className="h-7 rounded-md border border-stone-200 bg-white px-2 text-xs text-stone-700 focus:outline-none focus:ring-2 focus:ring-stone-300"
                aria-label="To date"
              />
              {hasDateRange && (
                <button
                  type="button"
                  data-testid="events-date-clear"
                  onClick={() => {
                    setFromDate("");
                    setToDate("");
                  }}
                  className="ml-1 inline-flex items-center text-[10.5px] text-stone-500 underline-offset-2 hover:text-stone-900 hover:underline"
                >
                  Clear
                </button>
              )}
            </div>
            {dateRangeInvalid && (
              <p
                data-testid="events-date-invalid"
                className="text-[11px] text-rose-600"
              >
                'From' must be on or before 'To'.
              </p>
            )}
            <div
              data-testid="events-range-picker"
              className="inline-flex items-center gap-2"
            >
              <div
                className={`inline-flex rounded-full border border-stone-200 bg-white p-1 text-xs ${
                  hasDateRange ? "opacity-50" : ""
                }`}
                title={hasDateRange ? "Disabled while a date range is set (export only)" : undefined}
              >
                {RANGE_OPTIONS.map((o) => (
                  <button
                    key={o.days}
                    type="button"
                    data-testid={`events-range-${o.label}`}
                    onClick={() => setDays(o.days)}
                    className={`px-3 py-1.5 rounded-full transition-colors ${
                      days === o.days
                        ? "bg-stone-900 text-white"
                        : "text-stone-600 hover:text-stone-900"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              <div className="inline-flex rounded-full border border-stone-200 bg-white p-1 text-xs">
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="events-export-events"
                  disabled={exporting || dateRangeInvalid}
                  onClick={() => handleExportCsv("events")}
                  className="h-7 px-2.5 rounded-full text-xs"
                >
                  {exporting ? (
                    <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Download className="mr-1 h-3.5 w-3.5" />
                  )}
                  Events
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="events-export-clicks"
                  disabled={exporting || dateRangeInvalid}
                  onClick={() => handleExportCsv("clicks")}
                  className="h-7 px-2.5 rounded-full text-xs"
                >
                  <Download className="mr-1 h-3.5 w-3.5" />
                  Clicks
                </Button>
              <Button
                variant="ghost"
                size="sm"
                data-testid="events-export-all"
                disabled={exporting || dateRangeInvalid}
                onClick={() => handleExportCsv("all")}
                className="h-7 px-2.5 rounded-full text-xs font-semibold text-emerald-700 hover:text-emerald-800"
              >
                <Download className="mr-1 h-3.5 w-3.5" />
                All
              </Button>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <p
            data-testid="events-error"
            className="mt-6 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700"
          >
            {error}
          </p>
        )}

        {/* Top-line stats */}
        <section className="mt-8 grid gap-4 md:grid-cols-3">
          <StatCard
            testId="events-total"
            icon={<Activity className="h-5 w-5" />}
            label="Total events"
            value={fmtNum(summary?.totals?.events)}
            loading={loading}
          />
          <StatCard
            testId="events-unique-users"
            icon={<Users className="h-5 w-5" />}
            label="Unique users"
            value={fmtNum(summary?.totals?.unique_users)}
            loading={loading}
          />
          <StatCard
            testId="events-conversion"
            icon={<BarChart3 className="h-5 w-5 text-emerald-600" />}
            label="Nudge conversion"
            value={conversion === null ? "—" : `${conversion}%`}
            loading={loading}
            footer="saved ÷ clicked"
          />
        </section>

        {/* Real-User Monitoring — P50/P95 page-load & check-completion
            latencies + share-funnel conversion. Renders empty-state
            cleanly when no RUM samples are in the window yet. */}
        <section
          data-testid="rum-section"
          className="mt-10 rounded-2xl border border-stone-200 bg-white p-5 shadow-sm"
        >
          <div className="flex items-center justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-stone-900">
                Real-User Monitoring
              </h2>
              <p className="mt-0.5 text-xs text-stone-500">
                Live latency + share-funnel conversion from production
                traffic.{rumData?.window_days
                  ? ` Rolling ${rumData.window_days}-day window.`
                  : ""}
              </p>
            </div>
            <span
              data-testid="rum-alerts-pill"
              className={
                "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ring-1 " +
                (rumAlertStatus?.enabled
                  ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                  : "bg-stone-100 text-stone-600 ring-stone-200")
              }
              title={rumAlertStatus?.enabled
                ? `P95 threshold ${rumAlertStatus.p95_threshold_ms}ms · conversion drop ${rumAlertStatus.conversion_drop_pct_threshold}%`
                : "Set RUM_ALERT_WEBHOOK_URL in backend/.env to enable"}
            >
              {rumAlertStatus?.enabled
                ? <>● Alerting on</>
                : <>○ Alerts off</>}
            </span>
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-3">
            <RumTile
              testid="rum-page-load"
              label="Page load (P50 / P95)"
              primary={
                rumData?.page_load?.samples
                  ? `${fmtNum(rumData.page_load.p50_ms)}ms / ${fmtNum(rumData.page_load.p95_ms)}ms`
                  : "—"
              }
              sub={
                rumData?.page_load?.samples
                  ? `${fmtNum(rumData.page_load.samples)} samples`
                  : "No samples yet"
              }
            />
            <RumTile
              testid="rum-check-completion"
              label="Check completion (P50 / P95)"
              primary={
                rumData?.check_completion?.samples
                  ? `${fmtNum(rumData.check_completion.p50_ms)}ms / ${fmtNum(rumData.check_completion.p95_ms)}ms`
                  : "—"
              }
              sub={
                rumData?.check_completion?.samples
                  ? `${fmtNum(rumData.check_completion.samples)} checks`
                  : "No samples yet"
              }
            />
            <RumTile
              testid="rum-share-funnel"
              label="Share → re-run conversion"
              primary={
                rumData?.share_funnel?.rerun_ctr_pct != null
                  ? `${rumData.share_funnel.rerun_ctr_pct}%`
                  : "—"
              }
              sub={
                rumData?.share_funnel
                  ? `${fmtNum(rumData.share_funnel.share_views)} views · ${fmtNum(rumData.share_funnel.rerun_clicks)} clicks · ${fmtNum(rumData.share_funnel.rerun_completions)} re-checks`
                  : "No samples yet"
              }
            />
          </div>

          {rumData?.page_load?.by_path?.length > 0 && (
            <div className="mt-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
                Top routes by load time
              </p>
              <ul
                data-testid="rum-by-path"
                className="mt-2 divide-y divide-stone-100 rounded-lg border border-stone-200 text-xs"
              >
                {rumData.page_load.by_path.map((row) => (
                  <li
                    key={row.path}
                    className="flex items-center justify-between gap-3 px-3 py-2"
                  >
                    <code className="truncate font-mono text-[11px] text-stone-700">
                      {row.path}
                    </code>
                    <span className="shrink-0 text-stone-600">
                      {fmtNum(row.p50)}ms / {fmtNum(row.p95)}ms · {fmtNum(row.count)} samples
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* Per-event sparkline table */}
        <section className="mt-10">
          <h2 className="text-xs uppercase tracking-wider text-stone-500">
            By event
          </h2>
          <div
            data-testid="events-table"
            className="mt-3 rounded-2xl border border-stone-200 bg-white shadow-sm overflow-hidden"
          >
            <table className="w-full text-sm">
              <thead className="bg-stone-50 text-stone-500 text-[11px] uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Event</th>
                  <th className="text-right px-4 py-3 font-medium">Total</th>
                  <th className="text-right px-4 py-3 font-medium">Users</th>
                  <th className="text-left px-4 py-3 font-medium">
                    Trend ({summary?.days || days}d)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6">
                      <div className="flex items-center gap-2 text-stone-400">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading…
                      </div>
                    </td>
                  </tr>
                )}
                {!loading && (summary?.events?.length || 0) === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-stone-400 text-sm">
                      No events yet in this window.
                    </td>
                  </tr>
                )}
                {!loading && summary?.events?.map((row) => (
                  <tr key={row.event_name} data-testid={`events-row-${row.event_name}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-stone-900">
                        {EVENT_LABELS[row.event_name] || row.event_name}
                      </div>
                      <div className="text-[11px] text-stone-400">
                        {row.event_name}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold tabular-nums">
                      {fmtNum(row.total)}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-stone-600">
                      {fmtNum(row.unique_users)}
                    </td>
                    <td className="px-4 py-3">
                      <Sparkline values={row.series} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Recent events feed */}
        <section className="mt-10">
          <h2 className="text-xs uppercase tracking-wider text-stone-500">
            Recent activity
          </h2>
          <ul
            data-testid="events-recent-list"
            className="mt-3 rounded-2xl border border-stone-200 bg-white shadow-sm divide-y divide-stone-100"
          >
            {loading && (
              <li className="flex items-center gap-2 px-4 py-4 text-stone-400">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </li>
            )}
            {!loading && recent.length === 0 && (
              <li className="px-4 py-4 text-stone-400 text-sm">
                No recent events.
              </li>
            )}
            {!loading && recent.map((r) => (
              <li key={r.id} className="px-4 py-3 text-sm">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-stone-900">
                    {EVENT_LABELS[r.event_name] || r.event_name}
                  </span>
                  <span className="text-[11px] text-stone-400 ml-auto">
                    {new Date(r.created_at).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-stone-500">
                  {r.user_email ? (
                    <span className="truncate">{r.user_email}</span>
                  ) : (
                    <span className="italic text-stone-400">anonymous</span>
                  )}
                  {r.properties && Object.keys(r.properties).length > 0 && (
                    <code className="text-[11px] bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5 max-w-full truncate">
                      {JSON.stringify(r.properties)}
                    </code>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      </div>
    </main>
  );
}

function StatCard({ testId, icon, label, value, footer, loading }) {
  return (
    <div
      data-testid={testId}
      className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm"
    >
      <div className="flex items-center justify-between text-stone-500">
        <span className="text-[11px] uppercase tracking-wider">{label}</span>
        {icon}
      </div>
      {loading ? (
        <div className="mt-3 flex items-center gap-2 text-stone-400">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="h-7 w-16 animate-pulse rounded bg-stone-200" />
        </div>
      ) : (
        <p className="mt-3 text-3xl font-semibold tracking-tight text-stone-900">
          {value}
        </p>
      )}
      {!loading && footer && (
        <p className="mt-1 text-[11px] text-stone-500">{footer}</p>
      )}
    </div>
  );
}

function Sparkline({ values }) {
  const safe = Array.isArray(values) && values.length > 0 ? values : [0];
  const max = Math.max(...safe, 1);
  const w = 160;
  const h = 32;
  const step = safe.length > 1 ? w / (safe.length - 1) : w;
  const points = safe
    .map((v, i) => `${(i * step).toFixed(1)},${(h - (v / max) * h).toFixed(1)}`)
    .join(" ");
  const area = `0,${h} ${points} ${w},${h}`;
  const lastHigh = safe[safe.length - 1] >= (safe[0] || 0);
  const stroke = lastHigh ? "#10b981" : "#f97316";
  const fill = lastHigh ? "rgba(16,185,129,0.12)" : "rgba(249,115,22,0.12)";
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="block">
      <polygon points={area} fill={fill} />
      <polyline
        points={points}
        fill="none"
        stroke={stroke}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}


function RumTile({ testid, label, primary, sub }) {
  return (
    <div
      data-testid={testid}
      className="rounded-xl border border-stone-200 bg-stone-50/50 p-4"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wider text-stone-500">
        {label}
      </p>
      <p className="mt-2 text-2xl font-semibold tracking-tight text-stone-900">
        {primary}
      </p>
      <p className="mt-1 text-[11px] text-stone-500">{sub}</p>
    </div>
  );
}
